import { memo, useContext, useEffect } from 'react';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Table, ComponentLoader, ToastContext } from 'cat-ecommerce-alloy';
import PropTypes from 'prop-types';
import { hasPriceAvailMapping } from '../../constants';
import { getCustomRenderers } from '../utils';
import AvailabilityHeader from '../../common/ColumnHeaders/AvailabilityHeader';
import QuantityHeader from '../../common/ColumnHeaders/QuantityHeader';
import TotalPriceHeader from '../../common/ColumnHeaders/TotalPriceHeader';
import LineItemNotes from '../common/LineItemNotes';
import PreferenceToggles from '../common/PreferenceToggles';
import { getInitialImgSettings, isLineItemNotesAvailable } from './utils';
import {
  LONG_TOAST_DELAY,
  USER_TYPE_GUEST
} from '@app/constants/commonConstants';

const CartItemsTable = memo(
  ({ displayAvailabityAndSourcingDetails, setTogglePrefs, showNotes }) => {
    const [t] = useTranslation();
    const toastCtx = useContext(ToastContext);
    const { createSuccessToast, createErrorToast } = toastCtx;

    const orderExtendAttribute = useSelector(
      s => s.orders.byId[s.orders.current]?.orderExtendAttribute
    );

    const ascendingOrderItems = useSelector(
      s => s.orders.byId[s.orders.current]?.ascendingOrderItems || []
    );

    const taxDisclaimerMessage = useSelector(
      s => s.orders.taxDisclaimerMessage
    );

    const cartTableData = ascendingOrderItems.map(item => ({
      ...item,
      isDeleted: !!item.packageError
    }));

    const { currencyNotation: currency, isOrderPriceAvailabilityLoading } =
      useSelector(s => s.orders);

    const {
      shoppingPreferencesData: { imageSettings = [] },
      toaster = {},
      error: prefsError
    } = useSelector(s => s.shoppingPreferences);
    const { userType } = useSelector(s => s.common);

    const initialImgSettings = getInitialImgSettings(imageSettings);
    const { displayOnPrint, displayOnScreen } = initialImgSettings;
    const isGuest = userType === USER_TYPE_GUEST;

    useEffect(() => {
      if (toaster.submitSuccess) {
        createSuccessToast(t('IMAGE_CONFIRMATION'), LONG_TOAST_DELAY);
      }
      if (toaster.submitSuccess === false) {
        createErrorToast(t('MA_PREF_CHANGES_NOT_SAVED'));
      }
    }, [t, createSuccessToast, createErrorToast, toaster]);

    const getTableData = () =>
      cartTableData.map(item => ({
        ...item,
        showRowNotes: !isGuest && isLineItemNotesAvailable(item),
        rowNotes: <LineItemNotes item={item} />,
        displayAvailabityAndSourcingDetails: displayAvailabityAndSourcingDetails
      }));

    const enrichSchema = mapping =>
      mapping.map(col => {
        if (col.id === 'delete') {
          return { ...col };
        }

        if (col.id === 'quantity') {
          return {
            ...col,
            label: <QuantityHeader translationKey={col.translationKey} />
          };
        }

        if (col.id === 'availability') {
          return {
            ...col,
            label: (
              <AvailabilityHeader
                translationKey={col.translationKey}
                isQuantityMergeEnabled={
                  orderExtendAttribute?.isQuantityMergeEnabled
                }
                ClassName="text-start"
              />
            )
          };
        }

        if (col.id === 'totalPrice') {
          return {
            ...col,
            label: (
              <TotalPriceHeader
                currency={currency}
                translationKey={col.translationKey}
                isQuantityMergeEnabled={
                  orderExtendAttribute?.isQuantityMergeEnabled
                }
                taxDisclaimerMessage={taxDisclaimerMessage}
              />
            )
          };
        }

        if (col.id === 'image') {
          return {
            ...col,
            className: cn({
              'd-none': !displayOnScreen,
              'd-md-table-cell': displayOnScreen,
              'd-print-none': !displayOnPrint,
              'd-print-table-cell': displayOnPrint
            })
          };
        }

        if (col.id === 'notes') {
          const val = cartTableData.find(data =>
            isLineItemNotesAvailable(data)
          );
          return {
            ...col,
            className: cn({
              'd-none d-print-table-cell': !!val,
              'd-none d-print-none': !val
            })
          };
        }

        return {
          ...col,
          label: col.translationKey ? t(col.translationKey) : col.label || ''
        };
      });

    const schema = enrichSchema(hasPriceAvailMapping);

    return (
      <div className={`mb-2 position-relative`} data-testid="order-items-table">
        {cartTableData.length > 0 && (
          <>
            {isOrderPriceAvailabilityLoading && <ComponentLoader />}
            <div>
              {!isGuest && !prefsError && (
                <PreferenceToggles
                  imageSettings={initialImgSettings}
                  setTogglePrefs={setTogglePrefs}
                  displayAvailabityAndSourcingDetails={
                    displayAvailabityAndSourcingDetails
                  }
                />
              )}
              <Table
                keyGenerator={i => i.orderItemId}
                schema={schema}
                data={getTableData()}
                customRenderers={getCustomRenderers()}
                showAllNotes={!isGuest && showNotes}
              />
            </div>
          </>
        )}
      </div>
    );
  }
);

CartItemsTable.displayName = 'CartItemsTable';

CartItemsTable.propTypes = {
  displayAvailabityAndSourcingDetails: PropTypes.bool,
  setTogglePrefs: PropTypes.func,
  showNotes: PropTypes.bool
};

export default CartItemsTable;
